/**
 * Référentiel des pays pour la saisie d'adresse.
 *
 * La source est `i18n-iso-countries` et NON la table `country` du back : cette
 * dernière n'a pas de code ISO (id, name en français, zone, lat, lng) et son
 * rôle est le zonage tarifaire Santé/Mobilité.
 *
 * Toute valeur rendue par ce module est un code ISO2 en majuscules, identique
 * à ce que produit Google Places (`address_components` → country.short_name).
 */
import countries from 'i18n-iso-countries';
import ar from 'i18n-iso-countries/langs/ar.json';
import en from 'i18n-iso-countries/langs/en.json';
import fr from 'i18n-iso-countries/langs/fr.json';
import zh from 'i18n-iso-countries/langs/zh.json';

countries.registerLocale(ar);
countries.registerLocale(en);
countries.registerLocale(fr);
countries.registerLocale(zh);

export interface CountryOption {
  code: string;
  label: string;
}

/** Les 4 langues du site. Toute autre locale retombe sur le français. */
const SUPPORTED_LOCALES = ['fr', 'en', 'ar', 'zh'];
const DEFAULT_LOCALE = 'fr';

/** `en-GB` → `en`, `de` → `fr` (défaut). */
const normalizeLocale = (locale?: string | null): string => {
  const base = (locale || DEFAULT_LOCALE).split('-')[0].toLowerCase();
  return SUPPORTED_LOCALES.includes(base) ? base : DEFAULT_LOCALE;
};

/**
 * Libellé traduit d'un code ISO2. Rend le code inchangé s'il est inconnu, pour
 * ne jamais masquer une donnée existante en base.
 */
export const getCountryLabel = (
  code: string | null | undefined,
  locale: string,
): string => {
  const normalized = (code || '').trim().toUpperCase();
  if (!normalized) return '';
  return countries.getName(normalized, normalizeLocale(locale)) || normalized;
};

/** Tous les pays, triés par libellé dans la langue demandée. */
export const getCountryOptions = (locale: string): CountryOption[] => {
  const lang = normalizeLocale(locale);
  const names = countries.getNames(lang);

  return Object.keys(names)
    .map((code) => ({ code, label: names[code] }))
    .sort((a, b) => a.label.localeCompare(b.label, lang));
};
